import { localStorageRemove } from '~/app/helpers/localStorage';
import { getDatabaseClient } from '~/data/db/DatabaseClient';
import { reset as resetSyncProgress } from '~/data/sync/syncProgressState';
import { Schemas } from '~/sqlite/schemas';
import QueryBuilder from '~/sqlite/services/queryBuilder';
import FileTransfer from '~/sqlite/services/fileTransfer';
import store from '~/store';

const OFFLINE_FILE_FOLDERS = [
  'categoryImages',
  'projectImages',
  'issueImages',
  'archives',
  'periodicCheckImages',
] as const;

const LOCAL_STORAGE_PREFIX = 'barde.';

/**
 * Wipe all device offline data on logout.
 * SQLite holds only the current user's cache; after logout offline mode is unsupported.
 */
export async function clearOfflineDataOnLogout(): Promise<void> {
  await clearSqliteTables();
  clearOfflineFiles();
  clearOfflineLocalStorageKeys();
  resetOfflineStoreState();
}

function clearSqliteTables(): Promise<void> {
  const db = getDatabaseClient()?.raw;
  if (!db) {
    return Promise.resolve();
  }
  const qb = new QueryBuilder();
  const tableNames = Object.keys(Schemas);
  return new Promise((resolve, reject) => {
    db.transaction(
      (tx) => {
        tableNames.forEach((tableName) => {
          const { query, params } = qb.truncate(tableName);
          tx.executeSql(query, params);
        });
      },
      (err) => {
        console.error('[clearOfflineDataOnLogout] SQLite wipe failed', err);
        reject(err);
      },
      () => resolve()
    );
  });
}

function clearOfflineFiles(): void {
  if (!window['cordova']?.file?.dataDirectory) {
    return;
  }
  const fileTransfer = new FileTransfer();
  OFFLINE_FILE_FOLDERS.forEach((folder) => {
    try {
      fileTransfer.removeFolder(folder);
    } catch (e) {
      console.error(`[clearOfflineDataOnLogout] failed to remove ${folder}`, e);
    }
  });
}

function clearOfflineLocalStorageKeys(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(LOCAL_STORAGE_PREFIX)) {
      continue;
    }
    const bare = key.slice(LOCAL_STORAGE_PREFIX.length);
    if (
      bare.startsWith('offline-') ||
      bare === 'permissions'
    ) {
      keysToRemove.push(bare);
    }
  }
  keysToRemove.forEach((bare) => localStorageRemove(bare));
}

function resetOfflineStoreState(): void {
  try {
    resetSyncProgress();
    const actions = store.getActions();
    actions.machines.fetchedFavorites([]);
    actions.machines.fetchedLastUsed([]);
    actions.projects.fetchedFavorites([]);
    actions.projects.fetchedLastUsed([]);
    actions.app.offline.setStatus(0);
    actions.app.offline.setSyncingOfflineData(false);
  } catch (e) {
    console.error('[clearOfflineDataOnLogout] store reset failed', e);
  }
}
